import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import axios from "axios";
import { API } from "@/App";

export const BookmarkEditModal = ({
  open, onOpenChange, form, setForm, collections,
  editingBookmark, getImageUrl, uploadingImage,
  onSave, onImageUpload, onDeleteImage, allTags,
}) => {
  const [tagInput, setTagInput] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [suggestingTags, setSuggestingTags] = useState(false);
  const inputRef = useRef(null);

  const currentTags = form.tags || [];

  const filteredSuggestions = (allTags || [])
    .filter(t => t.name.toLowerCase().includes(tagInput.toLowerCase()) && !currentTags.includes(t.name))
    .slice(0, 6);

  const addTag = (tag) => {
    const cleaned = tag.trim().toLowerCase().replace(/[^a-z0-9_-]/g, '');
    if (cleaned && !currentTags.includes(cleaned)) {
      setForm({ ...form, tags: [...currentTags, cleaned] });
    }
    setTagInput("");
    setShowSuggestions(false);
  };

  const removeTag = (tag) => {
    setForm({ ...form, tags: currentTags.filter(t => t !== tag) });
  };

  const suggestTags = async () => {
    if (!editingBookmark?.message_content) return;
    setSuggestingTags(true);
    try {
      const res = await axios.post(`${API}/bookmarks/suggest-tags`, {
        content: editingBookmark.message_content,
        annotation: form.annotation || "",
      });
      setAiSuggestions(res.data.suggested_tags || []);
    } catch (e) {
      console.error("Tag suggestion error:", e);
    } finally {
      setSuggestingTags(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="modal-dark sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-[#FDF8EE]">Edit Bookmark</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <label className="font-mono text-[9px] text-[#4A3A1A] uppercase tracking-[1px] block mb-1.5">Volume</label>
            <Select value={form.collection_id || "none"} onValueChange={(value) => setForm({ ...form, collection_id: value === "none" ? null : value })}>
              <SelectTrigger className="input-dark" data-testid="bookmark-collection-select">
                <SelectValue placeholder="Select volume" />
              </SelectTrigger>
              <SelectContent className="bg-[#1C1004] border-[rgba(232,160,48,0.15)]">
                <SelectItem value="none" className="text-[#8C7A5A]">Uncategorized</SelectItem>
                {collections.map((col) => (
                  <SelectItem key={col.collection_id} value={col.collection_id} className="text-[#F5ECD0]">
                    <div className="flex items-center gap-2"><span style={{ color: col.color }}>&#9672;</span>{col.name}</div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="font-mono text-[9px] text-[#4A3A1A] uppercase tracking-[1px] block mb-1.5">Note</label>
            <textarea value={form.annotation} onChange={(e) => setForm({ ...form, annotation: e.target.value })} placeholder="Add a note — why does this matter? Where will you use it?" className="input-dark w-full resize-none" rows={3} data-testid="bookmark-annotation-input" />
          </div>

          {/* Tags */}
          <div>
            <label className="font-mono text-[9px] text-[#4A3A1A] uppercase tracking-[1px] block mb-1.5">Tags</label>
            {currentTags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {currentTags.map(tag => (
                  <span key={tag} className="flex items-center gap-1 px-2 py-0.5 bg-[rgba(232,160,48,0.08)] border border-[rgba(232,160,48,0.15)]">
                    <span className="text-[9px] text-[#E8A030] font-mono">#{tag}</span>
                    <button onClick={() => removeTag(tag)} className="text-[#4A3A1A] hover:text-red-400 text-xs leading-none ml-0.5" data-testid={`remove-tag-${tag}`}>&times;</button>
                  </span>
                ))}
              </div>
            )}
            <div className="relative">
              <input
                ref={inputRef}
                value={tagInput}
                onChange={(e) => { setTagInput(e.target.value); setShowSuggestions(true); }}
                onFocus={() => setShowSuggestions(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && tagInput.trim()) { e.preventDefault(); addTag(tagInput); }
                  if (e.key === 'Backspace' && !tagInput && currentTags.length > 0) { removeTag(currentTags[currentTags.length - 1]); }
                }}
                placeholder={currentTags.length > 0 ? "Add more tags..." : "Type a tag and press Enter (e.g., strategy, rag)"}
                className="input-dark w-full text-sm"
                data-testid="tag-input"
              />
              {showSuggestions && tagInput && filteredSuggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-[#1C1004] border border-[rgba(232,160,48,0.15)] z-50 max-h-40 overflow-auto" data-testid="tag-suggestions">
                  {filteredSuggestions.map(s => (
                    <button
                      key={s.name}
                      onMouseDown={(e) => { e.preventDefault(); addTag(s.name); }}
                      className="w-full flex items-center justify-between px-3 py-2 hover:bg-[rgba(232,160,48,0.08)] transition-colors"
                      data-testid={`tag-suggestion-${s.name}`}
                    >
                      <span className="text-sm text-[#F5ECD0]">#{s.name}</span>
                      <span className="font-mono text-[8px] text-[#4A3A1A]">{s.count} uses</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* AI Tag Suggestions */}
            <button
              onClick={suggestTags}
              disabled={suggestingTags}
              className="w-full flex items-center justify-center gap-2 py-1.5 mt-2 border border-dashed border-[rgba(232,160,48,0.15)] hover:border-[rgba(232,160,48,0.3)] text-[#8C7A5A] hover:text-[#E8A030] transition-colors disabled:opacity-40"
              data-testid="suggest-tags-btn"
            >
              <span className="font-serif font-bold text-xs text-[#E8A030]">R</span>
              <span className="font-mono text-[9px] uppercase tracking-[1px]">
                {suggestingTags ? "Ryan is thinking..." : "Suggest Tags"}
              </span>
            </button>
            {aiSuggestions.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="ai-tag-suggestions">
                {aiSuggestions.filter(t => !currentTags.includes(t)).map(tag => (
                  <button
                    key={tag}
                    onClick={() => addTag(tag)}
                    className="flex items-center gap-1 px-2 py-0.5 bg-[rgba(122,184,160,0.08)] border border-[rgba(122,184,160,0.15)] hover:border-[rgba(122,184,160,0.3)] transition-colors"
                    data-testid={`ai-tag-${tag}`}
                  >
                    <span className="text-[9px] text-[#7AB8A0] font-mono">+ #{tag}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Images */}
          <div>
            <label className="font-mono text-[9px] text-[#4A3A1A] uppercase tracking-[1px] block mb-1.5">Images</label>
            {editingBookmark?.images?.length > 0 && (
              <div className="space-y-2 mb-3">
                {editingBookmark.images.map((img) => (
                  <div key={img.image_id} className="relative group border border-[rgba(232,160,48,0.1)] p-2">
                    <img src={getImageUrl(img.storage_path)} alt={img.original_filename} className="w-full max-h-40 object-contain bg-[#0A0600]" data-testid={`bookmark-image-${img.image_id}`} />
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="font-mono text-[8px] text-[#4A3A1A]">{img.original_filename}</span>
                      <button onClick={() => onDeleteImage(editingBookmark.bookmark_id, img.image_id)} className="font-mono text-[8px] text-red-400 hover:underline opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-image-${img.image_id}`}>Remove</button>
                    </div>
                    {img.ocr_text && (
                      <details className="mt-1.5">
                        <summary className="font-mono text-[8px] text-[#E8A030] cursor-pointer hover:underline uppercase tracking-[1px]">Extracted Text</summary>
                        <p className="mt-1 text-[10px] text-[#8C7A5A] bg-[rgba(232,160,48,0.03)] p-2 border border-[rgba(232,160,48,0.06)] max-h-24 overflow-auto whitespace-pre-wrap">{img.ocr_text}</p>
                      </details>
                    )}
                  </div>
                ))}
              </div>
            )}
            <label className={`block w-full border border-dashed border-[rgba(232,160,48,0.2)] hover:border-[rgba(232,160,48,0.4)] p-3 text-center cursor-pointer transition-colors ${uploadingImage ? "opacity-50 pointer-events-none" : ""}`} data-testid="image-upload-area">
              <input type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files?.[0] && editingBookmark) { onImageUpload(editingBookmark.bookmark_id, e.target.files[0]); e.target.value = ""; } }} data-testid="image-upload-input" />
              <span className="font-mono text-[9px] text-[#8C7A5A] uppercase tracking-[1px]">{uploadingImage ? "Uploading & Running OCR..." : "+ Attach Image (photo, screenshot, whiteboard)"}</span>
            </label>
          </div>
        </div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="btn-ghost">Cancel</button>
          <button onClick={onSave} className="btn-primary" data-testid="save-bookmark-btn">Save</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
